import type {
  StrategyRdDirection,
  StrategyRdEntryGuidance,
  StrategyRdMatch,
  StrategyRdMatchPath,
  StrategyRdPathEvent,
  StrategyRdPathEventKind,
  StrategyRdSidePolicy,
} from "../types/strategyRd";
import { strategyRdConceptChartLabel } from "./strategyRdConceptCopy";

export const STRATEGY_RD_SIDE_POLICY_OPTIONS: Array<{
  value: StrategyRdSidePolicy;
  label: string;
}> = [
  { value: "both", label: "Both" },
  { value: "long_only", label: "Long only" },
  { value: "short_only", label: "Short only" },
];

export function parseStrategyRdSidePolicy(raw: unknown): StrategyRdSidePolicy {
  const v = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  if (v === "long_only" || v === "short_only") return v;
  return "both";
}

function finiteNum(value: unknown): number | null {
  // Number(null) === 0 — never coerce null/undefined/"" into a fake price.
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function parseDirectionLoose(raw: unknown): StrategyRdDirection | null {
  const d = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  if (d === "long" || d === "short" || d === "neutral") return d;
  return null;
}

function parsePathEventKind(raw: unknown): StrategyRdPathEventKind | null {
  const k = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  if (
    k === "setup" ||
    k === "break" ||
    k === "rejection" ||
    k === "retest" ||
    k === "location_armed" ||
    k === "respect" ||
    k === "reject" ||
    k === "break_retest" ||
    k === "entry" ||
    k === "order_placed" ||
    k === "cancel"
  ) {
    return k;
  }
  return null;
}

export function parseStrategyRdPathEvent(raw: unknown): StrategyRdPathEvent | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const kind = parsePathEventKind(row.kind);
  const barTime =
    typeof row.bar_time === "string" ? row.bar_time.trim() : String(row.bar_time ?? "").trim();
  if (!kind || !barTime) return null;
  const barIndex = finiteNum(row.bar_index);
  const conceptId =
    typeof row.concept_id === "string" ? row.concept_id.trim() || null : null;
  const role = typeof row.role === "string" ? row.role.trim() || null : null;
  const order = typeof row.order === "string" ? row.order.trim() || null : null;
  return {
    kind,
    bar_time: barTime,
    direction: parseDirectionLoose(row.direction),
    price: finiteNum(row.price),
    bar_index: barIndex,
    level_high: finiteNum(row.level_high),
    level_low: finiteNum(row.level_low),
    concept_id: conceptId,
    role,
    ...(order ? { order } : {}),
  };
}

/**
 * Resting rule-entry orders to draw as dashed lines: from `order_placed` to the fill
 * (`entry`) or `cancel`, else to the path's last event.
 */
export function strategyRdPathOrderLines(
  path: Pick<StrategyRdMatchPath, "events"> | null | undefined
): Array<{ price: number; from: string; to: string; order: string; outcome: "filled" | "cancelled" | "open" }> {
  const events = path?.events ?? [];
  const out: Array<{
    price: number;
    from: string;
    to: string;
    order: string;
    outcome: "filled" | "cancelled" | "open";
  }> = [];
  events.forEach((ev, i) => {
    if (ev.kind !== "order_placed" || ev.price == null) return;
    const end = events.slice(i + 1).find((e) => e.kind === "entry" || e.kind === "cancel");
    const last = events[events.length - 1];
    out.push({
      price: ev.price,
      from: ev.bar_time,
      to: end?.bar_time ?? last?.bar_time ?? ev.bar_time,
      order: ev.order || "limit",
      outcome: end?.kind === "entry" ? "filled" : end?.kind === "cancel" ? "cancelled" : "open",
    });
  });
  return out;
}

/** Parse Product B′ match.path from scan / preview-path. */
export function parseStrategyRdMatchPath(raw: unknown): StrategyRdMatchPath | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const statusRaw = typeof row.status === "string" ? row.status.trim().toLowerCase() : "";
  const status = statusRaw === "ready" ? "ready" : "incomplete";
  const eventsRaw = Array.isArray(row.events) ? row.events : [];
  const events = eventsRaw
    .map(parseStrategyRdPathEvent)
    .filter((e): e is StrategyRdPathEvent => e != null);
  const setupBar =
    typeof row.setup_bar_time === "string"
      ? row.setup_bar_time.trim()
      : events.find((e) => e.kind === "setup")?.bar_time || null;
  const dir = parseDirectionLoose(row.direction);
  const stopLevel = asRecord(row.stop_level);
  const targetLevel = asRecord(row.target_level);
  return {
    status,
    entry_mode: typeof row.entry_mode === "string" ? row.entry_mode.trim() : "",
    side_policy: parseStrategyRdSidePolicy(row.side_policy),
    candle_type:
      typeof row.candle_type === "string" ? row.candle_type.trim() || null : null,
    setup_bar_time: setupBar || null,
    level_high: finiteNum(row.level_high),
    level_low: finiteNum(row.level_low),
    break_bar_time:
      typeof row.break_bar_time === "string" ? row.break_bar_time.trim() || null : null,
    break_direction:
      parseDirectionLoose(row.break_direction) === "short"
        ? "short"
        : parseDirectionLoose(row.break_direction) === "long"
          ? "long"
          : null,
    break_price: finiteNum(row.break_price),
    rejection_bar_time:
      typeof row.rejection_bar_time === "string"
        ? row.rejection_bar_time.trim() || null
        : null,
    retest_bar_time:
      typeof row.retest_bar_time === "string" ? row.retest_bar_time.trim() || null : null,
    entry_bar_time:
      typeof row.entry_bar_time === "string" ? row.entry_bar_time.trim() || null : null,
    entry_time: typeof row.entry_time === "string" ? row.entry_time.trim() || null : null,
    entry_price: finiteNum(row.entry_price),
    direction: dir === "long" || dir === "short" ? dir : null,
    location_fill:
      typeof row.location_fill === "string" ? row.location_fill.trim() || null : null,
    stop_price: finiteNum(row.stop_price),
    target_price: finiteNum(row.target_price),
    stop_anchor:
      typeof row.stop_anchor === "string" ? row.stop_anchor.trim() || null : null,
    target_mode:
      typeof row.target_mode === "string" ? row.target_mode.trim() || null : null,
    stop_level: stopLevel,
    target_level: targetLevel,
    invalid_reason:
      typeof row.invalid_reason === "string" ? row.invalid_reason.trim() || null : null,
    user_message:
      typeof row.user_message === "string" ? row.user_message.trim() || null : null,
    advice: typeof row.advice === "string" ? row.advice.trim() || null : null,
    events,
  };
}

/** Ready path SL/TP from BE — prefer over local Rules preview when present. */
export function strategyRdPathAppliedLevels(
  path: StrategyRdMatchPath | null | undefined
): { entry: number; stop: number; target: number; direction: "long" | "short" } | null {
  if (!path || path.status !== "ready") return null;
  const entry = path.entry_price;
  const stop = path.stop_price;
  const target = path.target_price;
  const direction = path.direction;
  if (
    entry == null ||
    stop == null ||
    target == null ||
    !Number.isFinite(entry) ||
    !Number.isFinite(stop) ||
    !Number.isFinite(target) ||
    (direction !== "long" && direction !== "short")
  ) {
    return null;
  }
  return { entry, stop, target, direction };
}

/** Human labels for path stop_anchor / target_mode (Rules + chart caption). */
export function strategyRdPathAnchorLabel(raw: string | null | undefined): string | null {
  const key = (raw || "").trim().toLowerCase();
  if (!key) return null;
  const labels: Record<string, string> = {
    signal_extreme: "Signal extreme",
    signal_close: "Signal close",
    level_extreme: "Level extreme",
    prior_day: "Prior day",
    prior_week: "Prior week",
    prior_month: "Prior month",
    swing: "Swing",
    prior_day_low: "Prior day low",
    prior_day_high: "Prior day high",
    prior_week_low: "Prior week low",
    prior_week_high: "Prior week high",
    prior_month_low: "Prior month low",
    prior_month_high: "Prior month high",
    swing_low: "Swing low",
    swing_high: "Swing high",
    r_multiple: "R multiple",
    atr_mult: "ATR ×",
    pct_price: "% of price",
    pips: "Pips",
    ticks: "Ticks",
  };
  return labels[key] || key.replace(/_/g, " ");
}

/** BE `stop_level` / `target_level` — structural evidence, not the offset stop/target price. */
export type StrategyRdPathLevelEvidence = {
  price: number;
  which: "high" | "low" | null;
  scope: string | null;
  barTime: string | null;
};

export function parseStrategyRdPathLevelEvidence(
  raw: Record<string, unknown> | null | undefined
): StrategyRdPathLevelEvidence | null {
  if (!raw) return null;
  const price = finiteNum(raw.price) ?? finiteNum(raw.level);
  if (price == null) return null;
  const whichRaw = typeof raw.which === "string" ? raw.which.trim().toLowerCase() : "";
  const which = whichRaw === "high" || whichRaw === "low" ? whichRaw : null;
  const scope =
    typeof raw.scope === "string" && raw.scope.trim()
      ? raw.scope.trim().toLowerCase()
      : null;
  const barTime =
    typeof raw.bar_time === "string" && raw.bar_time.trim()
      ? raw.bar_time.trim()
      : null;
  return { price, which, scope, barTime };
}

export function strategyRdPathLevelEvidenceLabel(
  evidence: StrategyRdPathLevelEvidence,
  fallbackAnchor?: string | null
): string {
  const whichBit = evidence.which === "high" ? "H" : evidence.which === "low" ? "L" : "";
  const scope = (evidence.scope || "").replace(/^prior_/, "");
  if (scope === "day") return whichBit ? `PD${whichBit}` : "Prior day";
  if (scope === "week") return whichBit ? `PW${whichBit}` : "Prior week";
  if (scope === "month") return whichBit ? `PM${whichBit}` : "Prior month";
  if (scope === "swing") return whichBit ? `Swing ${whichBit}` : "Swing";
  return strategyRdPathAnchorLabel(fallbackAnchor) || "Level";
}

/** Caption snippet when ready path has structural SL/TP. */
export function strategyRdPathLevelsCaption(
  path: StrategyRdMatchPath | null | undefined,
  formatPrice: (n: number) => string
): string | null {
  const levels = strategyRdPathAppliedLevels(path);
  if (!levels || !path) return null;
  const stopLabel = strategyRdPathAnchorLabel(path.stop_anchor);
  const targetLabel = strategyRdPathAnchorLabel(path.target_mode);
  const stopBit = stopLabel
    ? `SL ${formatPrice(levels.stop)} (${stopLabel})`
    : `SL ${formatPrice(levels.stop)}`;
  const targetBit = targetLabel
    ? `TP ${formatPrice(levels.target)} (${targetLabel})`
    : `TP ${formatPrice(levels.target)}`;
  return `Entry ${formatPrice(levels.entry)} · ${stopBit} · ${targetBit}`;
}

/**
 * Neutral setups (Inside bar, Coil, Doji, levels with Trade side Both) are checked as a long
 * and as a short; `meta.allowed_sides` lists the sides whose confluences passed.
 * Returns a card note only when one side was blocked, e.g. "Longs only".
 */
export function strategyRdAllowedSidesNote(
  match: Pick<StrategyRdMatch, "meta"> | null | undefined
): string | null {
  const raw = match?.meta?.allowed_sides;
  if (!Array.isArray(raw)) return null;
  const sides = new Set(raw.map((s) => String(s).trim().toLowerCase()));
  const long = sides.has("long");
  const short = sides.has("short");
  if (long && !short) return "Longs only: the short-side confluences failed";
  if (short && !long) return "Shorts only: the long-side confluences failed";
  return null;
}

/** Prefer path.direction when ready; else match.direction. */
export function strategyRdMatchDisplayDirection(
  match: Pick<StrategyRdMatch, "direction" | "path"> | null | undefined
): StrategyRdDirection {
  if (!match) return "neutral";
  if (match.path?.status === "ready" && (match.path.direction === "long" || match.path.direction === "short")) {
    return match.path.direction;
  }
  return match.direction;
}

/**
 * When any location-interaction chip is on, only ready paths are chart hits
 * (same as retest / rejection — incomplete arms are not shown).
 */
export function strategyRdMatchReadyForLocationChips(
  match: Pick<StrategyRdMatch, "path"> | null | undefined,
  hasLocationChips: boolean
): boolean {
  if (!hasLocationChips) return true;
  return match?.path?.status === "ready";
}

export function strategyRdPathInvalidReasonLabel(reason: string | null | undefined): string {
  const r = (reason || "").trim();
  if (!r) return "Path incomplete";
  if (r === "level_missing") {
    return "No level for break / retest / rejection. Use signal or next open, a level setup, or a location confluence";
  }
  if (r === "break_not_found") return "No break close in this window";
  if (r === "rejection_not_found") return "No rejection close in this window";
  if (r === "retest_not_found") return "Broke, no confirming retest in this window";
  if (r === "retest_invalidated") return "Retest invalidated";
  if (r === "bar_index_out_of_range") return "Setup bar missing from candles";
  if (r === "path_horizon_exceeded") return "No entry within Max wait of the setup";
  if (r === "entry_wait_exceeded") {
    return "The fill came after Max wait (counted from the setup close)";
  }
  if (r === "confluence_side_blocked") {
    return "Confluences passed only for the side Entry → Trade side does not allow";
  }
  if (r.startsWith("entry_tf_unavailable")) {
    const tf = r.split(":")[1]?.trim();
    return tf
      ? `Entry timeframe ${tf} unavailable for this window`
      : "Entry timeframe unavailable for this window";
  }
  // Custom rules (entry / stop / target).
  if (r === "entry_not_triggered") return "The entry rule never triggered within Max wait";
  if (r === "entry_cancelled") return "The entry rule's cancel condition hit before a fill";
  if (r === "entry_gap") return "A gap in the candles (session break or weekend) ended the wait";
  if (r.startsWith("rule_value_missing")) {
    const what = r.split(":")[1]?.trim();
    return what
      ? `The rule reads ${what.replace(".", " → ")}, which this trade doesn't have`
      : "The rule reads a price this trade doesn't have";
  }
  if (r === "stop_not_below_entry") return "The rule's stop landed above a long's entry";
  if (r === "stop_not_above_entry") return "The rule's stop landed below a short's entry";
  if (r.startsWith("filter_failed")) return "The setup filter rejected this setup";
  return r.replace(/_/g, " ");
}

/**
 * Incomplete-path banner copy: BE `user_message` → `advice` → FE humanize of `invalid_reason`.
 * Prefer BE text as-is when present.
 */
export function strategyRdPathIncompleteMessage(
  path: Pick<StrategyRdMatchPath, "invalid_reason" | "user_message" | "advice"> | null | undefined
): string | null {
  if (!path) return null;
  const userMessage = (path.user_message || "").trim();
  if (userMessage) return userMessage;
  const advice = (path.advice || "").trim();
  if (advice) return advice;
  const reason = (path.invalid_reason || "").trim();
  if (!reason) return null;
  return strategyRdPathInvalidReasonLabel(reason);
}

/**
 * Prefer top-level `entry_guidance.user_message`, else match `path.user_message`,
 * then advice / invalid_reason from guidance then path.
 */
export function strategyRdIncompleteEntryMessage(opts: {
  entryGuidance?: StrategyRdEntryGuidance | null;
  path?: Pick<StrategyRdMatchPath, "invalid_reason" | "user_message" | "advice"> | null;
}): string | null {
  const guidance = opts.entryGuidance;
  const path = opts.path;
  const guidanceUser = (guidance?.user_message || "").trim();
  if (guidanceUser) return guidanceUser;
  const pathUser = (path?.user_message || "").trim();
  if (pathUser) return pathUser;
  const guidanceAdvice = (guidance?.advice || "").trim();
  if (guidanceAdvice) return guidanceAdvice;
  const pathAdvice = (path?.advice || "").trim();
  if (pathAdvice) return pathAdvice;
  const guidanceReason = (guidance?.invalid_reason || "").trim();
  if (guidanceReason) return strategyRdPathInvalidReasonLabel(guidanceReason);
  const pathReason = (path?.invalid_reason || "").trim();
  if (pathReason) return strategyRdPathInvalidReasonLabel(pathReason);
  return null;
}

export function parseStrategyRdEntryGuidance(raw: unknown): StrategyRdEntryGuidance | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const invalid_reason =
    typeof row.invalid_reason === "string" ? row.invalid_reason.trim() || null : null;
  const advice = typeof row.advice === "string" ? row.advice.trim() || null : null;
  const user_message =
    typeof row.user_message === "string" ? row.user_message.trim() || null : null;
  if (!invalid_reason && !advice && !user_message) return null;
  return { invalid_reason, advice, user_message };
}

/** True when banner should show for guidance and/or an incomplete selected path. */
export function strategyRdShouldShowIncompleteEntryBanner(opts: {
  entryGuidance?: StrategyRdEntryGuidance | null;
  path?: Pick<StrategyRdMatchPath, "status" | "invalid_reason" | "user_message" | "advice"> | null;
}): boolean {
  if (opts.entryGuidance && strategyRdIncompleteEntryMessage({ entryGuidance: opts.entryGuidance })) {
    return true;
  }
  return opts.path?.status === "incomplete";
}

/** Short on-chart copy for incomplete paths. */
export function strategyRdPathIncompleteChartLabel(
  reason: string | null | undefined
): string | null {
  const r = (reason || "").trim();
  if (r === "level_missing") return "No level";
  if (r === "retest_invalidated") return "Retest invalidated";
  if (r === "retest_not_found") return "No retest";
  if (r === "rejection_not_found") return "No rejection";
  if (r === "break_not_found") return "No break";
  if (r === "path_horizon_exceeded") return "No confirm (Max wait)";
  if (r === "entry_wait_exceeded") return "Wait exceeded";
  if (r === "confluence_side_blocked") return "Side blocked";
  if (r === "entry_not_triggered") return "Not triggered";
  if (r === "entry_cancelled") return "Cancelled";
  if (r === "entry_gap") return "Gap";
  if (r.startsWith("rule_value_missing")) return "Rule value missing";
  if (r === "stop_not_below_entry" || r === "stop_not_above_entry") return "Stop wrong side";
  return null;
}

export function strategyRdPathEventChartLabel(
  kind: StrategyRdPathEventKind,
  entryMode?: string | null,
  opts?: {
    conceptId?: string | null;
    role?: string | null;
  }
): string {
  if (kind === "setup") return "Setup";
  if (kind === "break") return "Break";
  if (kind === "rejection") return "Reject";
  if (kind === "retest") return "Retest";
  if (kind === "location_armed") {
    return strategyRdLocationArmedChartLabel(opts?.conceptId, opts?.role);
  }
  if (kind === "respect") return "Respect";
  if (kind === "reject") {
    const id = (opts?.conceptId || "").trim().toLowerCase();
    const zoneBreakThrough =
      id === "fvg_overlap" ||
      id === "near_ob" ||
      id === "near_breaker" ||
      id.includes("fvg");
    return zoneBreakThrough ? "Break through" : "Reject";
  }
  if (kind === "break_retest") return "Break retest";
  if (kind === "order_placed") return "Order";
  if (kind === "cancel") return "Cancel";
  const mode = (entryMode || "").trim();
  if (kind === "entry") {
    if (mode === "next_open") return "Enter";
    return "Enter";
  }
  return kind;
}

/** Chart label for location_armed — zone check vs event confirm. */
export function strategyRdLocationArmedChartLabel(
  conceptId?: string | null,
  role?: string | null
): string {
  const id = (conceptId || "").trim().toLowerCase();
  const eventConfirm =
    (role || "").trim().toLowerCase() === "event_confirm" ||
    id === "prior_liq_sweep" ||
    id === "prior_liq_run" ||
    id === "bos" ||
    id === "choch";
  if (!id) return eventConfirm ? "Confirm" : "Loc check";
  if (id === "trendline" || id.includes("trendline")) return "TL check";
  if (id.includes("fvg")) return "FVG check";
  if (id === "near_breaker") return "BB check";
  if (id === "near_ob" || id.includes("order_block") || /(^|_)ob(_|$)/.test(id)) {
    return "OB check";
  }
  if (id === "near_swing") return "Swing check";
  if (id === "near_prior_sr") return "Prior check";
  if (id === "prior_liq_run" || id === "liq_run" || (id.includes("liq") && id.includes("run"))) {
    return eventConfirm ? "Run confirm" : "Run check";
  }
  if (id.includes("sweep") || id.includes("liq")) {
    return eventConfirm ? "Liq confirm" : "Sweep check";
  }
  if (id === "bos") return eventConfirm ? "BOS confirm" : "BOS check";
  if (id === "choch") return eventConfirm ? "CHoCH confirm" : "CHoCH check";
  const tag = strategyRdConceptChartLabel(id);
  if (eventConfirm) return tag ? `${tag} confirm` : "Confirm";
  return tag ? `${tag} check` : "Loc check";
}

/**
 * Entry candle for the chart entry / position-tool anchor: `entry_bar_time`.
 * `entry_time` is the fill instant (for close entries, the next candle's open) —
 * for durations only, never for placement.
 */
export function strategyRdMatchEntryBarTime(
  match: Pick<StrategyRdMatch, "bar_time" | "path">
): string {
  const path = match.path;
  const fromPath =
    path?.entry_bar_time?.trim() ||
    path?.events?.find((e) => e.kind === "entry")?.bar_time?.trim() ||
    path?.entry_time?.trim();
  return fromPath || match.bar_time;
}

/**
 * One Enter per hit — only when path is ready with an entry bar.
 * Incomplete / path_horizon_exceeded → setup may still draw, but no Enter.
 * barTime is the entry candle (`entry_bar_time`, = the entry event); for close-based
 * entries that candle's close is the fill. Never `entry_time` (next candle's open).
 */
export function strategyRdPathReadyEnter(
  path: StrategyRdMatchPath | null | undefined
): { barTime: string; price: number | null } | null {
  if (!path || path.status !== "ready") return null;
  const barTime =
    path.entry_bar_time?.trim() ||
    path.events?.find((e) => e.kind === "entry")?.bar_time?.trim() ||
    path.entry_time?.trim() ||
    "";
  if (!barTime) return null;
  const entryEv = path.events?.find((e) => e.kind === "entry");
  const price =
    path.entry_price != null && Number.isFinite(Number(path.entry_price))
      ? Number(path.entry_price)
      : entryEv?.price != null && Number.isFinite(Number(entryEv.price))
        ? Number(entryEv.price)
        : null;
  return { barTime, price };
}

/** Highlight the most informative path bar (entry → break → setup). */
export function strategyRdPathHighlightBarTime(
  path: StrategyRdMatchPath | null | undefined
): string | null {
  if (!path) return null;
  const events = strategyRdPathEventsNormalized(path);
  return (
    events.find((e) => e.kind === "entry")?.bar_time ||
    events.find((e) => e.kind === "respect" || e.kind === "reject" || e.kind === "break_retest")
      ?.bar_time ||
    events.find((e) => e.kind === "location_armed")?.bar_time ||
    events.find((e) => e.kind === "retest")?.bar_time ||
    events.find((e) => e.kind === "rejection")?.bar_time ||
    events.find((e) => e.kind === "break")?.bar_time ||
    events.find((e) => e.kind === "setup")?.bar_time ||
    path.entry_bar_time?.trim() ||
    path.rejection_bar_time?.trim() ||
    path.setup_bar_time?.trim() ||
    null
  );
}

export function strategyRdPathSetupZone(
  path: StrategyRdMatchPath | null | undefined
): { high: number; low: number; label: string } | null {
  if (!path) return null;
  const setupEvent = path.events.find((e) => e.kind === "setup");
  const high = path.level_high ?? setupEvent?.level_high ?? null;
  const low = path.level_low ?? setupEvent?.level_low ?? null;
  if (high == null || low == null || !Number.isFinite(high) || !Number.isFinite(low)) {
    return null;
  }
  const top = Math.max(high, low);
  const bottom = Math.min(high, low);
  if (!(top > bottom)) return null;
  return { high: top, low: bottom, label: "Setup range" };
}

const PATH_EVENT_KIND_ORDER: StrategyRdPathEventKind[] = [
  "setup",
  "break",
  "rejection",
  "retest",
  "location_armed",
  "respect",
  "reject",
  "break_retest",
  "entry",
];

function strategyRdPathEventsFromFlatFields(
  path: StrategyRdMatchPath
): StrategyRdPathEvent[] {
  const out: StrategyRdPathEvent[] = [];
  if (path.setup_bar_time) {
    out.push({
      kind: "setup",
      bar_time: path.setup_bar_time,
      direction: null,
      price: null,
      bar_index: null,
      level_high: path.level_high,
      level_low: path.level_low,
    });
  }
  if (path.break_bar_time) {
    out.push({
      kind: "break",
      bar_time: path.break_bar_time,
      direction: path.break_direction,
      price: path.break_price,
      bar_index: null,
    });
  }
  if (path.rejection_bar_time) {
    out.push({
      kind: "rejection",
      bar_time: path.rejection_bar_time,
      direction: path.direction,
      price: path.entry_price,
      bar_index: null,
    });
  }
  if (path.retest_bar_time) {
    out.push({
      kind: "retest",
      bar_time: path.retest_bar_time,
      direction: path.direction,
      price: null,
      bar_index: null,
    });
  }
  const entryTime = path.entry_bar_time?.trim() || path.entry_time?.trim();
  if (entryTime) {
    out.push({
      kind: "entry",
      bar_time: entryTime,
      direction: path.direction,
      price: path.entry_price,
      bar_index: null,
    });
  }
  return out;
}

/**
 * Chart / highlight timeline for a match path.
 * Prefer path.events only — do not invent break/retest from flat fields when
 * events already describe the path (e.g. signal_close → setup + entry).
 * Flat setup/break/retest/entry_* are a legacy fallback when events is empty.
 */
export function strategyRdPathEventsNormalized(
  path: StrategyRdMatchPath | null | undefined
): StrategyRdPathEvent[] {
  if (!path) return [];
  const fromEvents: StrategyRdPathEvent[] = [];
  const seenBarKeys = new Set<string>();
  for (const event of path.events) {
    if (!event.kind || !event.bar_time?.trim()) continue;
    const barTime = event.bar_time.trim();
    const barKey = `${event.kind}|${barTime}`;
    if (seenBarKeys.has(barKey)) continue;
    seenBarKeys.add(barKey);
    fromEvents.push({ ...event, bar_time: barTime });
  }
  const out =
    fromEvents.length > 0 ? fromEvents : strategyRdPathEventsFromFlatFields(path);
  out.sort(
    (a, b) => PATH_EVENT_KIND_ORDER.indexOf(a.kind) - PATH_EVENT_KIND_ORDER.indexOf(b.kind)
  );
  return out;
}

export type StrategyRdPathChartMarker = {
  barTime: string;
  kind: StrategyRdPathEventKind;
  direction: StrategyRdDirection | null;
  price: number | null;
  label: string;
};

/** Chart markers from path.events. Setup is a zone by default; Lab preview also labels the setup bar. */
export function strategyRdPathChartMarkers(
  path: StrategyRdMatchPath | null | undefined,
  opts?: { includeSetup?: boolean; setupLabel?: string | null }
): StrategyRdPathChartMarker[] {
  const events = strategyRdPathEventsNormalized(path);
  const fallbackDir =
    path?.direction === "long" || path?.direction === "short" ? path.direction : null;
  const markers: StrategyRdPathChartMarker[] = [];
  const setupLabel = opts?.setupLabel?.trim() || "Setup";
  const pathReady = path?.status === "ready";
  for (const event of events) {
    if (event.kind === "setup" && !opts?.includeSetup) continue;
    // One Enter per hit — never paint Enter on incomplete / horizon-exceeded paths.
    if (event.kind === "entry" && !pathReady) continue;
    // Entry event bar_time already equals entry_bar_time (the entry candle).
    const barTime = event.bar_time?.trim();
    if (!barTime) continue;
    const dir =
      event.direction === "long" || event.direction === "short"
        ? event.direction
        : fallbackDir;
    markers.push({
      barTime,
      kind: event.kind,
      direction: dir,
      price: event.price ?? null,
      label:
        event.kind === "setup"
          ? setupLabel
          : strategyRdPathEventChartLabel(event.kind, path?.entry_mode, {
              conceptId: event.concept_id || path?.location_fill,
              role: event.role,
            }),
    });
  }
  return markers;
}

/** Live preview shows a few examples; Scan remains the full cohort. */
export const STRATEGY_RD_PREVIEW_MATCH_LIMIT = 20;

export function takeStrategyRdPreviewExamples(
  matches: readonly StrategyRdMatch[] | null | undefined
): StrategyRdMatch[] {
  if (!matches?.length) return [];
  return matches.slice(0, STRATEGY_RD_PREVIEW_MATCH_LIMIT);
}

function mergePreviewPathMeta(
  prior: StrategyRdMatch["meta"],
  next: StrategyRdMatch["meta"]
): StrategyRdMatch["meta"] {
  const priorRow = prior && typeof prior === "object" ? prior : null;
  const nextRow = next && typeof next === "object" ? next : null;
  if (!priorRow && !nextRow) return next ?? prior;
  const merged = { ...(priorRow ?? {}), ...(nextRow ?? {}) };
  if (nextRow?.sr == null && priorRow?.sr != null) {
    merged.sr = priorRow.sr;
  }
  return merged;
}

/** Merge preview-path matches by setup bar_time + pattern_id. */
export function mergeStrategyRdPreviewPaths(
  prior: StrategyRdMatch[],
  enriched: StrategyRdMatch[]
): StrategyRdMatch[] {
  if (enriched.length === 0) return prior;
  const byKey = new Map(
    enriched.map((m) => [`${m.pattern_id}|${m.bar_time}`, m] as const)
  );
  return prior.map((m) => {
    const next = byKey.get(`${m.pattern_id}|${m.bar_time}`);
    if (!next) return m;
    return {
      ...m,
      ...next,
      chart: next.chart?.symbol ? next.chart : m.chart,
      path: next.path ?? m.path,
      direction: strategyRdMatchDisplayDirection(next),
      meta: mergePreviewPathMeta(m.meta, next.meta),
    };
  });
}
